import React, { useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  editStoreKnowledgeApi,
  deleteStoreKnowledgeApi,
} from '../../utils/apis';
import { CaretDown, PencilSimple, TrashSimple } from '@phosphor-icons/react';

import QuestionEditDrawer from '../GeneralSupportFlow/QuestionEditDrawer';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import NoDataFallback from '../common/NoDataFallback';

interface QuestionnaireProps {
  borderRadius?: string;
  border?: string;
  selectedStore: any;
  isStoreLoading: boolean;
  isNewQuestion: boolean;
  setIsNewQuestion: (value: boolean) => void;
  isGeneralSupport?: boolean;
  height?: string;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({
  selectedStore,
  isStoreLoading,
  isNewQuestion,
  setIsNewQuestion,
  isGeneralSupport = false,
  height = '100%',
  borderRadius = '',
  border = 'none',
}) => {
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [questionIndex, setQuestionIndex] = useState<number>(-1);
  const queryClient = useQueryClient();
  const { showAlert, alert, setAlert } = useAlert();
  const [deletingQuestionIds, setDeletingQuestionIds] = useState<number[]>([]);

  const { mutate: editStoreKnowledge, isPending: isEditPending } = useMutation({
    mutationFn: editStoreKnowledgeApi,
    onSuccess: () => {
      setIsEditDrawerOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['stores'],
      });
    },
    onError: (error: any) => {
      setIsEditDrawerOpen(false);
      showAlert(error.response.data.message, 'error');
    },
  });

  const { mutate: deleteStoreKnowledge, isPending: isDeletePending } =
    useMutation({
      mutationFn: deleteStoreKnowledgeApi,
      onMutate: variables => {
        setDeletingQuestionIds(prev => [...prev, Number(variables.id)]);
      },
      onSuccess: (_, variables) => {
        setDeletingQuestionIds(prev =>
          prev.filter(id => id !== Number(variables.id))
        );
        queryClient.invalidateQueries({
          queryKey: ['stores'],
        });
        showAlert('Question deleted successfully', 'success');
      },
      onError: (error: any, variables) => {
        setDeletingQuestionIds(prev =>
          prev.filter(id => id !== Number(variables.id))
        );
        showAlert(error.response.data.message, 'error');
      },
    });

  const handleDelete = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    deleteStoreKnowledge({
      method: 'quesandAnswer',
      storeId: selectedStore.id,
      id: id,
    });
  };

  const handleEdit = (qa: any, event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    setSelectedQuestion({
      id: qa.id,
      question: qa.ques,
      answer: qa.ans,
    });
    setQuestionIndex(index);
    setIsEditDrawerOpen(true);
  };

  const handleSaveEdit = (editedQuestion: {
    question: string;
    answer: string;
  }) => {
    editStoreKnowledge({
      storeId: selectedStore.id,
      id: selectedQuestion.id,
      question: editedQuestion.question,
      answer: editedQuestion.answer,
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        height: height,
        overflow: 'auto',
        border: border,
        borderRadius: borderRadius,
        backgroundColor: '#ffffff',
      }}
    >
      {isNewQuestion && (
        <Accordion
          defaultExpanded
          sx={{
            mb: 2,
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: '#DCE0E7',
            borderRadius: '6px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
          }}
        >
          <AccordionSummary
            expandIcon={
              <IconButton
                sx={{
                  width: '32px',
                  height: '32px',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#fff' },
                  ml: '8px',
                }}
              >
                <CaretDown size={16} />
              </IconButton>
            }
            sx={{
              bgcolor: '#5A4CE61A',
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mr: '8px',
              },
            }}
          >
            <Typography
              sx={{ fontSize: '14px', fontWeight: 500, color: '#1C1C1C' }}
            >
              New Question
            </Typography>
            {!isGeneralSupport && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <IconButton
                  onClick={() => setIsNewQuestion(false)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    bgcolor: '#E74C3C26',
                    '&:hover': { bgcolor: '#E74C3C26' },
                    mr: '8px',
                  }}
                >
                  <TrashSimple size={16} color="#E74C3C" />
                </IconButton>
              </Box>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  Question
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your question"
                  multiline
                  rows={2}
                  value={newQuestion}
                  onChange={e => setNewQuestion(e.target.value)}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  Answer
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your answer"
                  multiline
                  rows={4}
                  value={newAnswer}
                  onChange={e => setNewAnswer(e.target.value)}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {isStoreLoading ? (
        [...Array(3)].map((_, index) => (
          <Box key={`skeleton-${index}`} sx={{ mb: 2 }}>
            <Skeleton width="100%" height={100} />
          </Box>
        ))
      ) : selectedStore.storeknowledgeBase?.qna?.length === 0 ||
        !selectedStore.storeknowledgeBase?.qna ? (
        <NoDataFallback
          message="No Questionnaire available for this Store"
          description="Currently no questionnaire is available for this store"
        />
      ) : (
        selectedStore.storeknowledgeBase?.qna?.map((qa: any, index: number) => (
          <Accordion
            key={index}
            defaultExpanded
            sx={{
              mb: 2,
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: '#DCE0E7',
              borderRadius: '6px !important',
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={
                <IconButton
                  sx={{
                    width: '32px',
                    height: '32px',
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#fff' },
                    ml: '8px',
                  }}
                >
                  <CaretDown size={16} />
                </IconButton>
              }
              sx={{
                bgcolor: '#5A4CE61A',
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mr: '8px',
                },
              }}
            >
              <Typography
                sx={{ fontSize: '14px', fontWeight: 500, color: '#1C1C1C' }}
              >
                Question {index + 1}
              </Typography>
              {!isGeneralSupport && (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <IconButton
                    onClick={e => handleEdit(qa, e, index)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      bgcolor: '#fff',
                      '&:hover': { bgcolor: '#fff' },
                    }}
                  >
                    <PencilSimple size={16} />
                  </IconButton>
                  <IconButton
                    onClick={e => handleDelete(qa.id, e)}
                    disabled={
                      deletingQuestionIds.includes(qa.id) || isDeletePending
                    }
                    sx={{
                      width: '32px',
                      height: '32px',
                      mr: '8px',
                      bgcolor: '#E74C3C26',
                      '&:hover': { bgcolor: '#E74C3C26' },
                    }}
                  >
                    {deletingQuestionIds.includes(qa.id) ? (
                      <CircularProgress size={14} />
                    ) : (
                      <TrashSimple size={16} color="#E74C3C" />
                    )}
                  </IconButton>
                </Box>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{ fontSize: '12px', color: 'text.secondary' }}
                  >
                    Question
                  </Typography>
                  <Typography sx={{ p: 1 }}>{qa.ques}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{ fontSize: '12px', color: 'text.secondary' }}
                  >
                    Answer
                  </Typography>
                  <Typography sx={{ p: 1 }}>{qa.ans}</Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <QuestionEditDrawer
        open={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        selectedStore={selectedStore}
        questionData={selectedQuestion}
        questionIndex={questionIndex}
        onSave={handleSaveEdit}
        isPending={isEditPending}
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default Questionnaire;
